import { type ReactNode, useMemo, useState } from "react";
import type { EChartsCoreOption } from "echarts/core";
import { parseTs } from "../api/labels";
import { platformKeys, platformLabel as label } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { EChart, cssVar } from "../components/EChart";
import { Badge, Button, EmptyState, ErrorState, Panel, Skeleton, SkeletonRegion, Stack } from "@akaike/ui";
import { Seg } from "./foundation";
import { type Theme, useTheme } from "../lib/theme-store";
import { AnimatedNumber } from "../lib/motion";
import {
  useUsageBurnRateV1ProxyUsageBurnRateGet,
  useUsageByIpV1ProxyUsageByIpGet,
  useUsageDeadBySubnetV1ProxyUsageDeadBySubnetGet,
  useUsageIpProfileV1ProxyUsageIpIpGet,
  useUsageRetireV1ProxyUsageRetireGet,
  useUsageRightsizingV1ProxyUsageRightsizingGet,
} from "../api/generated/qcsControlPlane";
import type {
  BurnRow,
  DeadSubnetRow,
  FleetRightsizingOut,
  Platform,
  PlatformPeak,
  RetireRow,
  UsageIpRow,
} from "../api/generated/model";

// -- formatting ---------------------------------------------------------------
const fmtPct = (r: number | null | undefined): string =>
  r == null ? "—" : `${(r * 100).toFixed(1)}%`;

// GB to 2-3 significant figures (e.g. 0.88, 21.7, 486).
function fmtGb(g: number | null | undefined): string {
  if (g == null) return "—";
  const s = g >= 100 ? g.toFixed(0) : g >= 10 ? g.toFixed(1) : g.toFixed(2);
  return `${s} GB`;
}

const fmtInt = (n: number | null | undefined): string =>
  n == null ? "—" : Math.round(n).toLocaleString();

const fmtUsd = (n: number): string =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function ago(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = parseTs(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// Status -> stack/pill tone. Kept local so the drill-down timeline and the pills
// agree on colour without leaning on the pool page's own StatusPill.
function statusTone(status: string): "ok" | "warn" | "crit" | "idle" {
  if (status === "active") return "ok";
  if (status === "cooling") return "warn";
  if (status === "dead") return "crit";
  return "idle";
}

// -- small building blocks -----------------------------------------------------
function StatTile({
  lab,
  value,
  unit,
  tone,
  cap,
  capTone,
}: {
  lab: string;
  value: ReactNode;
  unit?: string;
  tone?: "ok" | "warn" | "crit" | "brand";
  cap?: ReactNode;
  capTone?: "ok" | "warn" | "crit";
}) {
  return (
    <div
      className="vital bg-panel-2 border border-line rounded-md"
      style={{ gap: 7, padding: "13px 14px" }}
    >
      <span className="lab">{lab}</span>
      <span className={`big${tone ? ` ${tone}` : ""}`} style={{ fontSize: 26 }}>
        {value}
        {unit && <small>{unit}</small>}
      </span>
      {cap != null && (
        <span
          className="faint text-[11px]"
          style={{ color: capTone ? `var(--${capTone})` : undefined }}
        >
          {cap}
        </span>
      )}
    </div>
  );
}

// The reframing note the whole feature hinges on: idle is the resting reserve, not waste.
function Note({ badge, children }: { badge: string; children: ReactNode }) {
  return (
    <div
      className="row"
      style={{
        gap: 10,
        alignItems: "flex-start",
        marginTop: 12,
        padding: "10px 13px",
        border: "1px solid var(--brand-line)",
        background: "var(--brand-dim)",
        borderRadius: "var(--r)",
        fontSize: 12,
        color: "var(--ink-2)",
      }}
    >
      <span
        className="mono"
        style={{
          flex: "none",
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: ".06em",
          color: "var(--brand)",
          border: "1px solid var(--brand-line)",
          borderRadius: 5,
          padding: "2px 6px",
        }}
      >
        {badge}
      </span>
      <div>{children}</div>
    </div>
  );
}

function EmptyBody({ loading, message }: { loading: boolean; message: string }) {
  return (
    <Panel.Body>
      {loading ? (
        <SkeletonRegion label="Loading">
          <Stack gap="sm" className="p-2">
            {Array.from({ length: 3 }, (_, i) => (
              <Skeleton key={i} h={24} />
            ))}
          </Stack>
        </SkeletonRegion>
      ) : (
        <EmptyState compact title={message} />
      )}
    </Panel.Body>
  );
}

// -- Band 1 : Fleet right-sizing (advisory, fleet-global) ----------------------
function PlatformPeakBars({ rows }: { rows: PlatformPeak[] }) {
  const max = Math.max(1, ...rows.map((r) => r.peak));
  return (
    <div className="col" style={{ gap: 10 }}>
      {rows.map((r) => (
        <div key={r.platform} className="row" style={{ gap: 12, alignItems: "center" }}>
          <span className={`plat ${r.platform}`} style={{ minWidth: 108 }}>
            {label(r.platform)}
          </span>
          <div className="bar" style={{ flex: 1 }}>
            <span className="ok" style={{ width: `${(r.peak / max) * 100}%` }} />
          </div>
          <span className="num mono" style={{ minWidth: 118, textAlign: "right", fontSize: 11.5 }}>
            peak {fmtInt(r.peak)}
            <span className="faint"> · p99 {fmtInt(r.p99)}</span>
          </span>
        </div>
      ))}
      {rows.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No per-platform peaks yet.</span>}
    </div>
  );
}

function FleetRightsizing() {
  const [windowDays, setWindowDays] = useState(30);
  const q = useUsageRightsizingV1ProxyUsageRightsizingGet(
    { window_days: windowDays },
    { query: { queryKey: ["proxy-usage-rightsizing", windowDays] } },
  );
  const d = q.data as FleetRightsizingOut | undefined;
  const genuineWaste = d ? d.dead_ips + d.never_used_ips : 0;
  const shedPct = d && d.fleet_size > 0 ? Math.round((d.headroom / d.fleet_size) * 100) : 0;

  return (
    <Panel className="mb-3.5">
      <Panel.Head title="Fleet right-sizing" className="flex-wrap gap-1.5">
        <Badge variant="outline-primary" className="ml-1.5">flat fee · fixed fleet</Badge>
        <Badge variant="outline" className="ml-0.5">$ advisory</Badge>
        <div className="flex-1" />
        <Seg
          value={String(windowDays)}
          onChange={(v) => setWindowDays(Number(v))}
          brand
          options={[
            { value: "7", label: "7d" },
            { value: "30", label: "30d" },
          ]}
        />
      </Panel.Head>
      {q.isError ? (
        <Panel.Body>
          <ErrorState compact title="Could not load fleet right-sizing." onRetry={() => q.refetch()} />
        </Panel.Body>
      ) : !d ? (
        <EmptyBody loading={q.isLoading} message="No usage data yet." />
      ) : (
        <Panel.Body>
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
            }}
          >
            <StatTile lab="fleet (present)" value={<AnimatedNumber value={d.fleet_size} format={fmtInt} />} cap="present IPs · flat fee" />
            <StatTile
              lab="peak concurrent"
              value={<AnimatedNumber value={d.peak_concurrent} format={fmtInt} />}
              tone="ok"
              cap={`p99 ${fmtInt(d.p99_concurrent)} · max simultaneously leased`}
            />
            <StatTile
              lab="headroom"
              value={<AnimatedNumber value={d.headroom} format={fmtInt} />}
              cap={`fleet - peak · could shed ≈${shedPct}% (advisory)`}
            />
            <StatTile
              lab="resting reserve"
              value={<AnimatedNumber value={d.resting_reserve} format={fmtInt} />}
              cap="resting reserve / headroom: NOT waste"
            />
            <StatTile
              lab="genuine waste"
              value={<AnimatedNumber value={genuineWaste} format={fmtInt} />}
              tone="crit"
              cap={`${fmtInt(d.dead_ips)} dead + ${fmtInt(d.never_used_ips)} never-used`}
            />
            {d.waste_advisory_usd > 0 && (
              <StatTile
                lab="advisory"
                value={<AnimatedNumber value={d.waste_advisory_usd} format={fmtUsd} />}
                cap={`≈ ${fmtUsd(d.cost_per_ip_day)}/IP-day · advisory only`}
              />
            )}
          </div>

          <div className="col mt-4 border-t border-line pt-3.5" style={{ gap: 8 }}>
            <span className="eyebrow">peak concurrent by platform</span>
            <PlatformPeakBars rows={d.per_platform} />
          </div>

          <Note badge="FRAMING">
            <b>Idle is the resting reserve the pool wants, never waste.</b> The plan is a flat fee for a
            fixed fleet, so the lever is <b>right-sizing</b>: peak concurrent demand ({fmtInt(d.peak_concurrent)})
            sits under the {fmtInt(d.fleet_size)} we pay for, leaving {fmtInt(d.headroom)} headroom. Cooldowns,
            velocity caps and spread rotation deliberately rest most IPs to protect reputation. Genuine waste is
            only <b>dead + never-used</b> ({fmtInt(genuineWaste)} IPs); the dollar figure is advisory.
          </Note>
        </Panel.Body>
      )}
    </Panel>
  );
}

// -- Band 2 : Reputation ops (per-platform) ------------------------------------
function DeadBySubnet({ platform }: { platform: Platform }) {
  const q = useUsageDeadBySubnetV1ProxyUsageDeadBySubnetGet(
    { platform },
    {
      query: {
        enabled: !!platform,
        queryKey: ["proxy-usage-dead-subnet", platform],
      },
    },
  );
  const rows = (q.data as DeadSubnetRow[] | undefined) ?? [];
  return (
    <Panel>
      <Panel.Head title="Dead by subnet">
        <div className="flex-1" />
        <span className="faint text-[11px]">refresh candidates, not idle</span>
      </Panel.Head>
      {q.isError ? (
        <Panel.Body>
          <ErrorState compact title="Could not load subnets." onRetry={() => q.refetch()} />
        </Panel.Body>
      ) : rows.length === 0 ? (
        <EmptyBody loading={q.isLoading} message="No dead-subnet data yet." />
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead>
              <tr>
                <th>Subnet /24</th>
                <th>ASN</th>
                <th className="r">IPs</th>
                <th className="r">Dead</th>
                <th className="r">Dead%</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const tone = r.dead_frac >= 0.3 ? "crit" : r.dead_frac >= 0.15 ? "warn" : "ok";
                return (
                  <tr key={r.subnet}>
                    <td className="mono num" style={{ fontSize: 11.5 }}>{r.subnet}</td>
                    <td className="faint" style={{ fontSize: 11.5 }}>{r.asn_org ?? "—"}</td>
                    <td className="r num">{fmtInt(r.ips)}</td>
                    <td className="r num" style={{ color: `var(--${tone})` }}>{fmtInt(r.dead)}</td>
                    <td className="r" style={{ minWidth: 120 }}>
                      <div
                        className="row"
                        style={{
                          gap: 8,
                          justifyContent: "flex-end",
                          alignItems: "center",
                        }}
                      >
                        <div className="bar" style={{ width: 56 }}>
                          <span
                            className={tone}
                            style={{
                              width: `${Math.min(100, r.dead_frac * 100)}%`,
                            }}
                          />
                        </div>
                        <span className="num" style={{ color: `var(--${tone})`, minWidth: 40 }}>
                          {fmtPct(r.dead_frac)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// Scored requests an IP needs before it can be ranked. The denominator holds out every
// bucket that is somebody else's fault, so on a platform that sheds load heavily the
// default can filter the whole roster out - and an empty table reads as "no bad IPs".
// The toggle is what tells those two apart.
const RETIRE_FLOORS = [20, 5, 0] as const;

function RetireList({ platform, onSelect }: { platform: Platform; onSelect: (ip: string) => void }) {
  const [floor, setFloor] = useState<number>(RETIRE_FLOORS[0]);
  const q = useUsageRetireV1ProxyUsageRetireGet(
    { platform, days: 30, min_scored_requests: floor },
    {
      query: {
        enabled: !!platform,
        queryKey: ["proxy-usage-retire", platform, floor],
      },
    },
  );
  const rows = (q.data as RetireRow[] | undefined) ?? [];
  return (
    <Panel>
      <Panel.Head title="Retire list">
        <div className="flex-1" />
        <span className="faint text-[11px]">min scored req</span>
        {RETIRE_FLOORS.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setFloor(n)}
            className="num"
            style={{
              background: "none",
              border: 0,
              padding: "0 5px",
              cursor: "pointer",
              fontSize: 11,
              color: n === floor ? "var(--info)" : "var(--faint)",
              textDecoration: n === floor ? "underline" : "none",
              textUnderlineOffset: 3,
            }}
          >
            {n === 0 ? "all" : n}
          </button>
        ))}
      </Panel.Head>
      {q.isError ? (
        <Panel.Body>
          <ErrorState compact title="Could not load retire list." onRetry={() => q.refetch()} />
        </Panel.Body>
      ) : rows.length === 0 ? (
        <EmptyBody loading={q.isLoading} message="No retire candidates yet." />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>IP</th>
                  <th>ASN</th>
                  <th className="r">Succ% used</th>
                  <th className="r">Block%</th>
                  <th className="r">Scored req</th>
                  <th className="r">Active d</th>
                  <th className="r">Data</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.ip}>
                    <td>
                      <button
                        type="button"
                        className="mono"
                        onClick={() => onSelect(r.ip)}
                        style={{
                          background: "none",
                          border: 0,
                          padding: 0,
                          cursor: "pointer",
                          color: "var(--info)",
                          textDecoration: "underline",
                          textUnderlineOffset: 2,
                          fontSize: 11.5,
                        }}
                      >
                        {r.ip}
                      </button>
                    </td>
                    <td className="faint" style={{ fontSize: 11.5 }}>{r.asn_org ?? "—"}</td>
                    <td
                      className="r num"
                      style={
                        r.success_rate_when_used != null && r.success_rate_when_used < 0.5
                          ? { color: "var(--crit)" }
                          : undefined
                      }
                    >
                      {fmtPct(r.success_rate_when_used)}
                    </td>
                    <td className="r num faint">{fmtPct(r.block_rate)}</td>
                    <td className="r num faint">{fmtInt(r.scored_requests)}</td>
                    <td className="r num">{fmtInt(r.active_days)}</td>
                    <td className="r num">{fmtGb(r.gb)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="faint" style={{ fontSize: 10.5, padding: "6px 14px" }}>
            Ranks TRANSPORT reliability: success-rate over the requests that actually tested this
            IP's tunnel, so target blocks, empty-200 load-sheds and proxy-provider refusals are all
            out of the denominator (Scored req is what's left). Not raw yield, which spread-rotation
            flattens to noise. IPs with too few scored requests to rank are held off the list.
          </div>
        </>
      )}
    </Panel>
  );
}

// `_theme` is a real input even though the body never reads it: the option is built from
// resolved CSS vars, so it has to be rebuilt when the theme flips.
function burnOption(rows: BurnRow[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  const crit = cssVar("--crit", "#d83a3a");
  const warn = cssVar("--warn", "#d77a14");
  const days = rows.map((r) => r.day.slice(5));
  return {
    grid: { left: 8, right: 12, top: 12, bottom: 6, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
    },
    xAxis: {
      type: "category",
      data: days,
      boundaryGap: false,
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
    },
    yAxis: {
      type: "value",
      scale: true,
      splitNumber: 3,
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
      splitLine: { lineStyle: { color: line, type: "dashed" } },
    },
    series: [
      {
        name: "dead",
        type: "line",
        data: rows.map((r) => r.dead),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: crit, width: 2 },
        itemStyle: { color: crit },
        areaStyle: { color: cssVar("--crit-dim", "rgba(216,58,58,.1)") },
      },
      {
        name: "cooling",
        type: "line",
        data: rows.map((r) => r.cooling),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: warn, width: 2 },
        itemStyle: { color: warn },
      },
    ],
  };
}

function BurnRate({ platform }: { platform: Platform }) {
  const q = useUsageBurnRateV1ProxyUsageBurnRateGet(
    { platform, days: 30 },
    {
      query: { enabled: !!platform, queryKey: ["proxy-usage-burn", platform] },
    },
  );
  // memoised so the `?? []` fallback does not hand the option memo a fresh array every render
  const rows = useMemo(() => (q.data as BurnRow[] | undefined) ?? [], [q.data]);
  const { theme } = useTheme();
  const option = useMemo(() => burnOption(rows, theme), [rows, theme]);
  return (
    <Panel>
      <Panel.Head title="Burn rate">
        <div className="flex-1" />
        <div className="legend text-[11px]">
          <i className="crit">dead</i>
          <i className="warn">cooling</i>
        </div>
      </Panel.Head>
      {q.isError ? (
        <Panel.Body>
          <ErrorState compact title="Could not load burn rate." onRetry={() => q.refetch()} />
        </Panel.Body>
      ) : rows.length === 0 ? (
        <EmptyBody loading={q.isLoading} message="No burn-rate data yet." />
      ) : (
        <Panel.Body>
          <div className="faint" style={{ fontSize: 11, marginBottom: 8 }}>
            dead + cooling IPs per day: when to ask the provider for fresh IPs
          </div>
          <EChart option={option} height={170} />
        </Panel.Body>
      )}
    </Panel>
  );
}

// -- Band 3 : Usage visibility + per-IP table ----------------------------------
function ByIpTable({
  platform,
  onSelect,
}: {
  platform: Platform;
  onSelect: (ip: string) => void;
}) {
  const q = useUsageByIpV1ProxyUsageByIpGet(
    { platform, days: 30 },
    {
      query: { enabled: !!platform, queryKey: ["proxy-usage-by-ip", platform] },
    },
  );
  const rows = (q.data as UsageIpRow[] | undefined) ?? [];

  const columns = useMemo<AnyColumnDef<UsageIpRow>[]>(() => {
    const col = createColumnHelper<UsageIpRow>();
    return [
      col.accessor("ip", {
        header: "IP",
        cell: ({ row }) => (
          <button
            type="button"
            className="mono"
            onClick={() => onSelect(row.original.ip)}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              cursor: "pointer",
              color: "var(--info)",
              textDecoration: "underline",
              textUnderlineOffset: 2,
              fontSize: 11.5,
            }}
            title="Open single-IP drill-down"
          >
            {row.original.ip}
          </button>
        ),
        meta: { minWidth: 150 },
      }),
      col.accessor("subnet", {
        header: "Subnet",
        cell: ({ row }) => (
          <span className="mono faint" style={{ fontSize: 11 }}>{row.original.subnet ?? "—"}</span>
        ),
        meta: { width: 150 },
      }),
      col.accessor("gb", {
        header: "Data",
        cell: ({ row }) => fmtGb(row.original.gb),
        meta: { align: "right", cellClassName: "num", width: 100 },
      }),
      col.accessor("success_rate_when_used", {
        header: "Succ% used",
        cell: ({ row }) => {
          const v = row.original.success_rate_when_used;
          return (
            <span style={v != null && v < 0.5 ? { color: "var(--crit)" } : undefined}>{fmtPct(v)}</span>
          );
        },
        meta: { align: "right", cellClassName: "num", width: 110 },
      }),
      col.accessor("block_rate", {
        header: "Block%",
        cell: ({ row }) => {
          const v = row.original.block_rate;
          return (
            <span style={v != null && v >= 0.3 ? { color: "var(--warn)" } : undefined}>{fmtPct(v)}</span>
          );
        },
        meta: { align: "right", cellClassName: "num faint", width: 90 },
      }),
      col.accessor("active_days", {
        header: "Active d",
        cell: ({ row }) => fmtInt(row.original.active_days),
        meta: { align: "right", cellClassName: "num", width: 90 },
      }),
      col.accessor("requests", {
        header: "Requests",
        cell: ({ row }) => fmtInt(row.original.requests),
        meta: { align: "right", cellClassName: "num faint", width: 100 },
      }),
      col.accessor("last_used", {
        header: "Last used",
        cell: ({ row }) => <span className="faint" style={{ fontSize: 11.5 }}>{ago(row.original.last_used)}</span>,
        meta: { align: "right", width: 110, exportValue: (r) => r.last_used },
      }),
    ];
  }, [onSelect]);

  return (
    <Panel className="mb-3.5">
      <Panel.Head title="Per-IP usage">
        <span className="faint num text-[11px] ml-1.5">{fmtInt(rows.length)} IPs</span>
        <div className="flex-1" />
        <span className="faint text-[11px]">click an IP → single-IP drill-down</span>
      </Panel.Head>
      {q.isError ? (
        <Panel.Body>
          <ErrorState compact title="Could not load per-IP usage." onRetry={() => q.refetch()} />
        </Panel.Body>
      ) : (
        <Panel.Body tight>
          <DataTable
            tableKey="proxy-usage-ip"
            columns={columns}
            data={rows}
            getRowId={(r) => r.ip}
            globalPlaceholder="Search IP / subnet…"
            csvFilename={`proxy-usage-${platform}`}
            pinFirstColumn
            emptyMessage="No usage data yet."
            isLoading={q.isLoading}
          />
        </Panel.Body>
      )}
    </Panel>
  );
}

// -- single-IP drill-down (modal) ----------------------------------------------
interface IpProfileSeries {
  day: string;
  platform: string;
  requests: number;
  successes: number;
  success_rate: number | null;
  block_rate: number | null;
  gb: number;
}
interface IpProfileStatus {
  day: string;
  platform: string;
  status: string;
}
interface IpProfile {
  ip: string;
  dim: {
    subnet: string | null;
    country: string | null;
    city: string | null;
    asn: string | null;
    asn_org: string | null;
    provider: string | null;
  };
  series: IpProfileSeries[];
  status: IpProfileStatus[];
}

// The endpoint's OpenAPI schema is a bare object, so orval types the response as
// Record<string, unknown> and IpProfile above is this panel's own contract for it.
// Check the fields the panel actually reads instead of asserting the shape: a
// response that does not match falls through to the same empty state as no data.
function isIpProfile(
  value: Record<string, unknown>,
): value is Record<string, unknown> & IpProfile {
  return (
    typeof value.ip === "string" && Array.isArray(value.series) && Array.isArray(value.status)
  );
}

function ipSeriesOption(series: IpProfileSeries[], _theme: Theme): EChartsCoreOption {
  const ink2 = cssVar("--ink-2", "#444");
  const faint = cssVar("--ink-3", "#888");
  const line = cssVar("--line", "#e5e5e5");
  const bg = cssVar("--panel", "#fff");
  const mono = cssVar("--f-mono", "ui-monospace, monospace");
  const info = cssVar("--info", "#1f82c4");
  const ok = cssVar("--ok", "#1f9d57");
  const days = series.map((s) => s.day.slice(5));
  return {
    grid: { left: 8, right: 12, top: 14, bottom: 6, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: bg,
      borderColor: line,
      borderWidth: 1,
      padding: [6, 9],
      textStyle: { color: ink2, fontSize: 11, fontFamily: mono },
    },
    xAxis: {
      type: "category",
      data: days,
      boundaryGap: false,
      axisLine: { lineStyle: { color: line } },
      axisTick: { show: false },
      axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
    },
    yAxis: [
      {
        type: "value",
        scale: true,
        splitNumber: 3,
        axisLabel: { color: faint, fontSize: 9, fontFamily: mono },
        splitLine: { lineStyle: { color: line, type: "dashed" } },
      },
      {
        type: "value",
        min: 0,
        max: 1,
        splitNumber: 2,
        axisLabel: {
          color: faint,
          fontSize: 9,
          fontFamily: mono,
          formatter: (v: number) => `${Math.round(v * 100)}%`,
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "requests",
        type: "line",
        yAxisIndex: 0,
        data: series.map((s) => s.requests),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: info, width: 2 },
        itemStyle: { color: info },
        areaStyle: { color: cssVar("--info-dim", "rgba(31,130,196,.1)") },
      },
      {
        name: "success rate",
        type: "line",
        yAxisIndex: 1,
        data: series.map((s) => s.success_rate),
        smooth: true,
        showSymbol: false,
        lineStyle: { color: ok, width: 2 },
        itemStyle: { color: ok },
      },
    ],
  };
}

function StatusTimeline({ status }: { status: IpProfileStatus[] }) {
  if (status.length === 0) return null;
  return (
    <div className="col" style={{ gap: 5 }}>
      <span className="eyebrow">status timeline</span>
      <div className="row" style={{ gap: 2, alignItems: "stretch" }}>
        {status.map((s, i) => (
          <div
            key={`${s.day}-${i}`}
            title={`${s.day}: ${s.status}`}
            style={{
              flex: 1,
              height: 9,
              borderRadius: 2,
              background: `var(--${statusTone(s.status)})`,
            }}
          />
        ))}
      </div>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <span className="faint mono" style={{ fontSize: 10 }}>{status[0]?.day}</span>
        <span className="faint mono" style={{ fontSize: 10 }}>{status[status.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function IpDrillDown({
  ip,
  platform,
  onClose,
}: {
  ip: string;
  platform: string;
  onClose: () => void;
}) {
  const q = useUsageIpProfileV1ProxyUsageIpIpGet(
    ip,
    { days: 30 },
    { query: { queryKey: ["proxy-usage-ip-profile", ip] } },
  );
  const prof = q.data && isIpProfile(q.data) ? q.data : undefined;

  // The IP can serve several platforms; focus the charts on the one whose table
  // was clicked, but fall back to whatever platforms the profile actually holds.
  const platforms = useMemo(() => {
    const set = new Set<string>();
    for (const s of prof?.series ?? []) set.add(s.platform);
    return [...set];
  }, [prof]);
  const [focus, setFocus] = useState(platform);
  const active = platforms.includes(focus) ? focus : (platforms[0] ?? platform);

  // memoised: an unmemoised filter is a fresh array every render, so the option memo below
  // never hits and EChart re-applies the option (notMerge) on every parent render
  const series = useMemo(
    () => (prof?.series ?? []).filter((s) => s.platform === active),
    [prof, active],
  );
  const statusRows = (prof?.status ?? []).filter((s) => s.platform === active);
  const { theme } = useTheme();
  const option = useMemo(() => ipSeriesOption(series, theme), [series, theme]);

  // Per-platform rollup (this IP is one physical slot, health is per-platform).
  const perPlatform = useMemo(() => {
    const acc = new Map<
      string,
      {
        requests: number;
        successes: number;
        gb: number;
        lastRate: number | null;
        lastBlock: number | null;
      }
    >();
    for (const s of prof?.series ?? []) {
      const cur = acc.get(s.platform) ?? {
        requests: 0,
        successes: 0,
        gb: 0,
        lastRate: null,
        lastBlock: null,
      };
      cur.requests += s.requests;
      cur.successes += s.successes;
      cur.gb += s.gb;
      cur.lastRate = s.success_rate;
      cur.lastBlock = s.block_rate;
      acc.set(s.platform, cur);
    }
    return [...acc.entries()].map(([p, v]) => ({ platform: p, ...v }));
  }, [prof]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(0,0,0,.5)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: 16,
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <Panel
        style={{ width: "100%", maxWidth: 760, marginTop: 32 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head className="flex-wrap gap-2.5">
          <span className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{ip}</span>
          {prof?.dim && (
            <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
              {prof.dim.subnet && <Chip>subnet {prof.dim.subnet}</Chip>}
              {prof.dim.asn_org && <Chip>ASN {prof.dim.asn_org}</Chip>}
              {prof.dim.country && <Chip>country {prof.dim.country}</Chip>}
              {prof.dim.city && <Chip>city {prof.dim.city}</Chip>}
              {prof.dim.provider && <Chip>provider {prof.dim.provider}</Chip>}
            </div>
          )}
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={onClose} aria-label="close">
            ✕
          </Button>
        </Panel.Head>

        {q.isError ? (
          <Panel.Body>
            <ErrorState compact title="Could not load this IP." onRetry={() => q.refetch()} />
          </Panel.Body>
        ) : !prof ? (
          <EmptyBody loading={q.isLoading} message="No profile for this IP yet." />
        ) : (
          <Panel.Body className="flex flex-col gap-4">
            {platforms.length > 1 && (
              <Seg
                value={active}
                onChange={setFocus}
                brand
                options={platforms.map((p) => ({ value: p, label: label(p) }))}
              />
            )}

            <div className="col" style={{ gap: 5 }}>
              <span className="eyebrow">daily requests &amp; success rate, {label(active)}</span>
              {series.length === 0 ? (
                <span className="muted" style={{ fontSize: 12 }}>No daily series for this platform.</span>
              ) : (
                <>
                  <div className="legend text-[11px]">
                    <i className="info">requests</i>
                    <i className="ok">success rate</i>
                  </div>
                  <EChart option={option} height={180} />
                </>
              )}
            </div>

            <StatusTimeline status={statusRows} />

            <div className="col" style={{ gap: 6 }}>
              <span className="eyebrow">platforms this IP served</span>
              <div className="overflow-x-auto">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th className="r">Requests</th>
                      <th className="r">Succ% used</th>
                      <th className="r">Block%</th>
                      <th className="r">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perPlatform.map((p) => (
                      <tr key={p.platform}>
                        <td><span className={`plat ${p.platform}`}>{label(p.platform)}</span></td>
                        <td className="r num">{fmtInt(p.requests)}</td>
                        <td
                          className="r num"
                          style={p.lastRate != null && p.lastRate < 0.5 ? { color: "var(--crit)" } : undefined}
                        >
                          {fmtPct(p.lastRate)}
                        </td>
                        <td className="r num faint">{fmtPct(p.lastBlock)}</td>
                        <td className="r num">{fmtGb(p.gb)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Panel.Body>
        )}
      </Panel>
    </div>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return (
    <span className="mono text-[10.5px] text-ink-2 border border-line bg-panel rounded-[6px] px-2 py-0.5">
      {children}
    </span>
  );
}

// -- the section wrapper -------------------------------------------------------
export function ProxyUsage() {
  const keys = platformKeys();
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [selectedIp, setSelectedIp] = useState<string | null>(null);

  // platformKeys() is filled at boot; if this section mounts before the first
  // platform is chosen, adopt the first available key once it appears.
  const activePlatform = platform ?? keys[0] ?? null;

  return (
    <>
      <FleetRightsizing />

      <div className="flex items-center gap-2.5 px-3.5 py-[9px] mb-3.5 rounded-md border border-line">
        <span className="text-[12.5px] font-semibold whitespace-nowrap text-foreground">Reputation &amp; usage</span>
        <span className="faint text-[11px] ml-2">per platform</span>
        <div className="flex-1" />
        {keys.length > 0 && activePlatform ? (
          <Seg
            value={activePlatform}
            onChange={setPlatform}
            brand
            options={keys.map((k) => ({ value: k, label: label(k) }))}
          />
        ) : (
          <div className="seg">
            <button disabled>no platforms</button>
          </div>
        )}
      </div>

      {activePlatform && (
        <>
          <div className="grid g-2 mb-3.5">
            <RetireList platform={activePlatform} onSelect={setSelectedIp} />
            <DeadBySubnet platform={activePlatform} />
          </div>

          <div className="mb-3.5">
            <BurnRate platform={activePlatform} />
          </div>

          <ByIpTable platform={activePlatform} onSelect={setSelectedIp} />
        </>
      )}

      {selectedIp && activePlatform && (
        <IpDrillDown ip={selectedIp} platform={activePlatform} onClose={() => setSelectedIp(null)} />
      )}
    </>
  );
}
